import { cn } from "../../lib/utils.js";

/**
 * How much of a phase is left, drawn as the record's outer ring.
 *
 * `fraction` is `1` at the start of the phase and `0` when it runs out; `null`
 * means the phase has no measurable length, and then no ring is drawn at all —
 * a ring against a guessed denominator is a progress indicator that is
 * confidently wrong.
 */
export type VinylCountdown = {
  /** How much of the phase remains, `1`…`0`, or `null` when unmeasurable. */
  fraction: number | null;
  /** Whether time is nearly out — the ring and the label say so together. */
  urgent: boolean;
};

/**
 * Where the countdown ring is drawn, in the 100-unit viewBox.
 *
 * **Outermost when the record is still; two rings in when it is turning** — the
 * spinning highlight takes the outside then, so the two never share a radius and
 * the eye never has to decide which of two arcs is the clock.
 */
const RING_RADIUS = { still: 45, turning: 35 } as const;
const RING_STROKE = 4;

/**
 * The record — spinning while a clip plays, or counting a phase down.
 *
 * Inline SVG themed by CSS custom properties rather than hardcoded fills, so the
 * phone and the ten-foot display can differ without forking the artwork — that
 * is the rule in `.claude/rules/frontend.md`, and it is why every colour below
 * is `currentColor` or a theme token.
 *
 * # One record, three uses
 *
 * The mark on the front door ([`Logo`](../Logo.js)), the disc that turns while a
 * clip plays, and the countdown ([`PhaseTimer`](./PhaseTimer.js)) are **one
 * drawing**. Two implementations of one shape disagree; the question is only
 * when — and a clock that is *nearly* the logo is worse than one that plainly is
 * not it.
 *
 * # It can spin *and* count, and only one part moves
 *
 * A clip playing and a phase running out are two different facts, and 20d's
 * second pass puts both on one disc rather than on two:
 *
 * | | still | turning |
 * |---|---|---|
 * | outermost | the countdown ring | the **highlight**, rotating |
 * | two rings in | — | the countdown ring |
 *
 * ⚠️ **Only the highlight rotates**, never the whole SVG: carrying the countdown
 * ring around with the disc destroys the one thing it means — where in the phase
 * we are. And it is one `<g>` on a CSS rotation behind `motion-safe:`, so the
 * idle cost on a television browser is a compositor transform and nothing else.
 *
 * 📌 The ring **moves inward** when the record starts turning. That is a visible
 * change between phases and it is the point: the outside belongs to whichever of
 * the two is moving.
 *
 * # It is decoration, and says so
 *
 * The state it depicts — a clip playing, a phase running out — is announced by
 * the surrounding UI in words. This carries `aria-hidden`, because a screen
 * reader reading out a spinning disc, or a shrinking arc, is noise on top of the
 * sentence that already said it. [`PhaseTimer`](./PhaseTimer.js) owns the
 * `role="timer"` and the spoken seconds.
 *
 * Reduced motion is handled globally in `styles/index.css`, which kills the
 * animation for anyone who asked for that. The disc still renders; it just does
 * not turn, and the ring steps rather than sliding.
 */
export function Vinyl({
  spinning = false,
  size = 96,
  countdown = null,
  className,
}: {
  /** Whether a clip is playing right now. Ignored while `countdown` is set. */
  spinning?: boolean;
  /** Edge length in pixels. */
  size?: number;
  /**
   * Draw the disc as a clock: the outer ring depletes clockwise from twelve, and
   * the label grows to carry the number the caller centres over it.
   */
  countdown?: VinylCountdown | null;
  className?: string;
}) {
  const counting = countdown !== null;
  // The label carries two digits when this is a clock, so it grows — and the
  // spindle hole goes, because a hole through the middle of the number is a
  // record detail winning an argument against legibility.
  const labelRadius = counting ? 28 : 14;
  const ringRadius = spinning ? RING_RADIUS.turning : RING_RADIUS.still;
  const ringLength = 2 * Math.PI * ringRadius;
  // Grooves sit between the label and the ring. Fewer, further out on the clock:
  // the same four radii would run under the digits.
  const grooves = counting ? (spinning ? [40, 31] : [40, 36, 32]) : [40, 33, 26, 19];

  return (
    <svg
      data-testid="vinyl"
      data-spinning={spinning}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
      className={cn(
        "text-flash-500",
        // A plain record turns as a whole; a clock turns only its highlight,
        // below. Nothing here reads layout either way.
        spinning && !counting && "motion-safe:animate-spin",
        className,
      )}
      style={spinning && !counting ? { animationDuration: "2.6s" } : undefined}
    >
      {/* The disc. */}
      <circle cx="50" cy="50" r="48" fill="var(--color-stage-800)" />

      {/* Grooves. Three or four rather than a texture: at phone size more reads
          as moiré, and at three metres they vanish either way. */}
      {grooves.map((r) => (
        <circle
          key={r}
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.22"
          strokeWidth="1"
        />
      ))}

      {countdown !== null && countdown.fraction !== null && (
        <g
          data-testid="vinyl-countdown"
          // From twelve o'clock, so the ring empties towards where it began —
          // which is what makes "nearly gone" readable without reading the
          // number.
          transform="rotate(-90 50 50)"
        >
          {/* The track the ring runs on. Faint, and the reason a nearly-spent
              ring still reads as *a ring that was full*. */}
          <circle
            cx="50"
            cy="50"
            r={ringRadius}
            fill="none"
            strokeWidth={RING_STROKE}
            className="stroke-white/10"
          />
          <circle
            cx="50"
            cy="50"
            r={ringRadius}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={ringLength}
            strokeDashoffset={ringLength * (1 - countdown.fraction)}
            className={cn(
              countdown.urgent ? "stroke-wrong-500" : "stroke-flash-500",
              // One property, twice a second, and nothing reads layout — the
              // television reflows nothing.
              "motion-safe:transition-[stroke-dashoffset] motion-safe:duration-500 motion-safe:ease-linear",
            )}
          />
        </g>
      )}

      {/* The label, and — on a record that is not a clock — the spindle hole
          that makes it read as a record rather than as a loading spinner. */}
      <circle
        cx="50"
        cy="50"
        r={labelRadius}
        className={countdown?.urgent ? "fill-wrong-500" : "fill-current"}
      />
      {!counting && <circle cx="50" cy="50" r="3" fill="var(--color-stage-950)" />}

      {/* One highlight, off-centre — the only thing that makes the rotation
          visible at all on a disc that is otherwise radially symmetric.

          On a **clock** it is the outermost ring and it is the part that turns,
          which is why it gets its own `<g>`: the disc under it must not move.
          ⚠️ `transformOrigin` is in user units (`50px` = the centre of a 100-unit
          viewBox), which is what an SVG element resolves it against with or
          without `transform-box` — the reading a television's older WebKit
          agrees with. */}
      {(!counting || spinning) && (
        <g
          data-testid={counting ? "vinyl-highlight" : undefined}
          className={cn(counting && spinning && "motion-safe:animate-spin")}
          style={
            counting && spinning
              ? { animationDuration: "2.6s", transformOrigin: "50px 50px" }
              : undefined
          }
        >
          <path
            d="M50 6a44 44 0 0 1 31.1 12.9"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.7"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>
      )}
    </svg>
  );
}
